import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from '../Empty';
import { Icon } from '../Icon';
import { Skeleton } from '../Skeleton';
import styles from './DataTable.module.css';

/**
 * Reusable, config-driven data table.
 *
 * Designed to be the canonical table component for Athena pages — pass it
 * a `columns` config + a `data` array and it handles header rendering,
 * sortable column cycling, optional row selection, and an optional toolbar
 * slot above the table.
 *
 * Sort cycle: each click on a sortable header advances asc → desc → null
 * (unsorted). Sort state is fully controlled by the parent so it works
 * with both client-side and server-side sorting.
 *
 * Ported from Themis `components/ui/DataTable/` (Epic 11.1). The JSX is
 * verbatim; only the CSS layer was retargeted onto Athena's design tokens.
 *
 * Nested rows (optional): pass `getRowChildren(row, depth)` to make a
 * parent expandable. Combined with controlled `expandedIds` /
 * `onExpandedChange`, the body walks the tree depth-first, auto-renders
 * a disclosure chevron in the first column, and indents the chevron cell
 * by `depth * indentPerDepth` px. The `column.render(row, ctx)` second
 * arg surfaces `{ depth, isExpanded, toggleExpand, hasChildren }` so
 * consumers can adapt cell content per level. Backwards compatible —
 * existing flat consumers ignoring the second arg keep working.
 *
 * Props:
 *   data              — array of row objects (top-level only when nested)
 *   columns           — array of { key, label, sortable?, width?, align?, render? }
 *   rowKey            — field name on each row used as React key (default 'id')
 *   sort              — { column, direction } | null  (controlled)
 *   onSortChange      — (column, direction) => void; called with (null, null) on cycle complete
 *   selectable        — boolean; adds checkbox column at far left
 *   selected          — array of rowKey values currently selected
 *   onSelectionChange — (ids) => void
 *   onRowClick        — (row) => void; optional row click handler
 *   toolbar           — ReactNode rendered above the table (typically <TableToolbar />)
 *   pagination        — ReactNode rendered below the table inside the card
 *                       (typically <TablePagination />). Stays pinned to the
 *                       bottom of the card while the table body scrolls.
 *   emptyMessage      — string shown when data is empty (default 'No data found').
 *                       Legacy/simple path; prefer `emptyState` for richer slots.
 *   emptyState        — optional object for a richer empty state, shape:
 *                       { icon?, title?, description? }
 *                       Athena-specific (not in Themis). When provided, it
 *                       wins over `emptyMessage`. CTA buttons live in the
 *                       toolbar's `actions` slot, not in the empty state.
 *   loading           — boolean; renders a simple skeleton row when true
 *   className         — optional wrapper class
 *   getRowChildren    — (row, depth) => row[] | null  (optional)
 *                       Returns the children of `row`. Null/empty = leaf.
 *                       Triggers nested-row rendering when provided.
 *   expandedIds       — array of rowKey values currently expanded (controlled)
 *   onExpandedChange  — (ids: string[]) => void; called on chevron toggle
 *   indentPerDepth    — number of px added to the disclosure cell per
 *                       depth level (default 24)
 *   disclosureColumnKey — key of the column that owns the auto-rendered
 *                         chevron + indent (default: first column's key)
 *   rowClassName      — (row, depth) => string | undefined  (optional)
 *                       Returned class is appended to the row's existing
 *                       classes (after .row / .rowSelected / .rowClickable).
 *                       Lets consumers apply per-row or per-depth styling
 *                       (e.g. distinct backgrounds per tree level) without
 *                       wrapping the table. Default: no extra class.
 */
const DataTable = ({
  data = [],
  columns = [],
  rowKey = 'id',
  sort = null,
  onSortChange,
  selectable = false,
  selected = [],
  onSelectionChange,
  onRowClick,
  toolbar,
  pagination,
  emptyMessage = 'No data found',
  emptyState = null,
  loading = false,
  className = '',
  getRowChildren,
  expandedIds = [],
  onExpandedChange,
  indentPerDepth = 24,
  disclosureColumnKey,
  rowClassName,
}) => {
  // Indeterminate state on the header checkbox needs an imperative ref —
  // it can't be set via React props.
  const headerCheckboxRef = useRef(null);
  const allSelected = data.length > 0 && data.every((row) => selected.includes(row[rowKey]));
  const someSelected = !allSelected && data.some((row) => selected.includes(row[rowKey]));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  // --- Column resize (handles inline inside each <th>) ---
  // Each <th> renders its own resize handle as an absolute-positioned
  // child at `right: -3.5px`, so the handle moves with the cell during
  // horizontal scroll, window resize, and the drag itself. No JS coord
  // math is needed — the browser handles all positioning via flex/grid
  // layout. The earlier overlay approach was off by `scrollLeft` pixels
  // once the table grew past the container; this layout-driven approach
  // is robust by construction.
  const [colWidths, setColWidths] = useState({});
  const tableRef = useRef(null);
  const scrollRef = useRef(null);
  const dragRef = useRef(null);

  const onResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const table = tableRef.current;
    if (!table) return;
    const headerRow = table.querySelector('thead tr');
    if (!headerRow) return;

    // Map `columns` → actual <th> cells (skipping the checkbox cell).
    const cells = Array.from(headerRow.children);
    const resizable = cells.filter((c) => !c.classList.contains(styles.checkboxCell));
    const colIdx = columns.findIndex((c) => c.key === colKey);
    const th = resizable[colIdx];
    if (!th) return;

    // The drag behaves as a splitter between the dragged column and the
    // last column ("filler"). Pixels given up by one are absorbed by the
    // other; every other column is untouched. `positionHandles` never
    // places a handle on the last column (see line ~91), so the filler
    // is guaranteed to be different from the dragged one.
    const fillerIdx = columns.length - 1;
    const fillerKey = columns[fillerIdx]?.key;
    const fillerTh = resizable[fillerIdx];
    if (!fillerTh || fillerIdx === colIdx) return;

    // Snapshot every cell to its current pixel width. Seed widths are
    // percentages, so without this step the browser would redistribute
    // freed space across all `%` columns mid-drag. Locking everything
    // in pixels keeps the other columns frozen while the splitter works.
    let totalWidth = 0;
    cells.forEach((cell) => {
      const w = cell.getBoundingClientRect().width;
      cell.style.transition = 'none';
      cell.style.width = w + 'px';
      totalWidth += w;
    });
    table.style.width = totalWidth + 'px';

    const MIN = 40;
    const startDragWidth = th.getBoundingClientRect().width;
    const startFillerWidth = fillerTh.getBoundingClientRect().width;

    dragRef.current = {
      colKey,
      fillerKey,
      startX: e.clientX,
      startDragWidth,
      startFillerWidth,
      startTableWidth: totalWidth,
      th,
      fillerTh,
      table,
      // Used by the narrowing path to decide how much of the released
      // pixels go to the table (shrinking it back toward container)
      // vs. to the filler.
      scrollEl: scrollRef.current,
    };

    const onMouseMove = (moveEvt) => {
      const d = dragRef.current;
      if (!d) return;
      const delta = moveEvt.clientX - d.startX;

      // Splitter feel: the dragged column's right edge tracks the cursor
      // exactly. The MIN floor on the dragged column is its only clamp.
      const newDragWidth = Math.max(MIN, d.startDragWidth + delta);
      const draggedGrowth = newDragWidth - d.startDragWidth;

      if (draggedGrowth >= 0) {
        // Widening: every other column (including the filler) stays
        // frozen; the table grows by the same amount. The horizontal
        // scroll on .tableScroll absorbs the overflow — visually the
        // table grows under the cursor instead of squeezing anything.
        d.th.style.width = newDragWidth + 'px';
        d.fillerTh.style.width = d.startFillerWidth + 'px';
        d.table.style.width = (d.startTableWidth + draggedGrowth) + 'px';
      } else {
        // Narrowing: release pixels to the TABLE first (shrink it back
        // toward the container width if there's overflow from prior
        // widening), then to the FILLER. This avoids the Themis bug
        // where widen-then-narrow ratcheted the filler wider every
        // cycle without ever shrinking the table back. With this fix
        // a widen-50 → narrow-50 cycle returns to the exact starting
        // state.
        const releasedPixels = -draggedGrowth;                 // positive
        const containerWidth = d.scrollEl ? d.scrollEl.clientWidth : d.startTableWidth;
        const tableOverflow = Math.max(0, d.startTableWidth - containerWidth);
        const shrinkTable = Math.min(releasedPixels, tableOverflow);
        const growFiller = releasedPixels - shrinkTable;

        d.th.style.width = newDragWidth + 'px';
        d.fillerTh.style.width = (d.startFillerWidth + growFiller) + 'px';
        d.table.style.width = (d.startTableWidth - shrinkTable) + 'px';
      }
    };

    const onMouseUp = () => {
      const d = dragRef.current;
      if (d) {
        // Clear the transient `transition: none` hints from every cell.
        const row = d.th.parentElement;
        const allCells = Array.from(row.children);
        allCells.forEach((cell) => { cell.style.transition = ''; });

        // Commit every resizable column's current pixel width. This keeps
        // sum-of-colWidths in sync with what's on screen, so when React
        // re-renders and replaces the inline `table.style.width` with the
        // computed sum (see `tableStyle` in render), the table doesn't
        // snap back from a widening.
        const resizableCells = allCells.filter(
          (c) => !c.classList.contains(styles.checkboxCell),
        );
        const newWidths = {};
        columns.forEach((col, i) => {
          const cell = resizableCells[i];
          if (cell) newWidths[col.key] = cell.getBoundingClientRect().width;
        });
        setColWidths((prev) => ({ ...prev, ...newWidths }));
      }
      dragRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columns]);

  const handleHeaderClick = (column) => {
    if (!column.sortable || !onSortChange) return;
    const isCurrent = sort?.column === column.key;
    let nextDir;
    if (!isCurrent) {
      nextDir = 'asc';
    } else if (sort.direction === 'asc') {
      nextDir = 'desc';
    } else {
      nextDir = null; // cycle returns to unsorted
    }
    if (nextDir === null) {
      onSortChange(null, null);
    } else {
      onSortChange(column.key, nextDir);
    }
  };

  const handleHeaderKeyDown = (e, column) => {
    if (!column.sortable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleHeaderClick(column);
    }
  };

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(data.map((row) => row[rowKey]));
    }
  };

  const toggleRow = (id, e) => {
    if (!onSelectionChange) return;
    e.stopPropagation();
    if (selected.includes(id)) {
      onSelectionChange(selected.filter((s) => s !== id));
    } else {
      onSelectionChange([...selected, id]);
    }
  };

  const totalColumns = columns.length + (selectable ? 1 : 0);

  // Disclosure column = the one that owns the chevron + per-depth indent.
  // Defaults to the first declared column. Consumers can override via
  // `disclosureColumnKey` (e.g., when a checkbox / icon column should
  // remain flush left).
  //
  // Only enabled when the table is actually nested (`getRowChildren`
  // provided). Otherwise flat tables would render a phantom chevron
  // spacer + inline `padding-left` in the first column, indenting its
  // content well past the normal cell padding. Gating on nesting lets
  // flat tables' first column use the standard `.cell` padding.
  const nestingEnabled = typeof getRowChildren === 'function';
  const resolvedDisclosureKey = nestingEnabled
    ? (disclosureColumnKey ?? columns[0]?.key)
    : undefined;

  const handleToggleExpand = (id) => {
    if (!onExpandedChange) return;
    if (expandedIds.includes(id)) {
      onExpandedChange(expandedIds.filter((x) => x !== id));
    } else {
      onExpandedChange([...expandedIds, id]);
    }
  };

  // Recursive row renderer. Walks rows in document order, emits one
  // <tr> per visited node, and recurses into children when expanded.
  // Each <tr> uses a composite key (parentId chain + own id) so React
  // doesn't reuse keys when the same id appears at multiple depths
  // (shouldn't happen, but defensive).
  const renderRowTree = (rows, depth, parentPath = '') => {
    if (!rows || rows.length === 0) return null;
    const fragments = [];

    rows.forEach((row) => {
      const id = row[rowKey];
      const trKey = parentPath ? `${parentPath}/${id}` : String(id);
      const isSelected = selected.includes(id);
      const children = getRowChildren ? getRowChildren(row, depth) : null;
      const hasChildren = Array.isArray(children) && children.length > 0;
      const isExpanded = hasChildren && expandedIds.includes(id);
      const ctx = {
        depth,
        isExpanded,
        toggleExpand: () => handleToggleExpand(id),
        hasChildren,
      };

      const consumerRowClass = rowClassName?.(row, depth) ?? '';
      fragments.push(
        <tr
          key={trKey}
          className={`${styles.row} ${isSelected ? styles.rowSelected : ''} ${
            onRowClick ? styles.rowClickable : ''
          } ${depth > 0 ? styles[`depth-${depth}`] ?? '' : ''} ${consumerRowClass}`}
          onClick={() => onRowClick?.(row)}
        >
          {selectable && (
            <td className={`${styles.cell} ${styles.checkboxCell}`}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={isSelected}
                onChange={(e) => toggleRow(id, e)}
                onClick={(e) => e.stopPropagation()}
                aria-label="Select row"
              />
            </td>
          )}
          {columns.map((column) => {
            const cellClasses = [
              styles.cell,
              column.align ? styles[`align-${column.align}`] : '',
              column.key === resolvedDisclosureKey ? styles.cellDisclosure : '',
            ]
              .filter(Boolean)
              .join(' ');
            const isDisclosureCell = column.key === resolvedDisclosureKey;
            // Compute per-cell render output. New `render(row, ctx)`
            // signature; single-arg consumers (the existing 4) ignore
            // the second arg.
            const content = column.render ? column.render(row, ctx) : row[column.key];

            if (!isDisclosureCell) {
              return (
                <td key={column.key} className={cellClasses}>
                  {content}
                </td>
              );
            }

            // Disclosure cell — pads left for depth and slots in a
            // chevron when the row has children. The chevron itself
            // stops propagation so it doesn't fire onRowClick.
            return (
              <td
                key={column.key}
                className={cellClasses}
                style={{ paddingLeft: `calc(var(--spacing-lg) + ${depth * indentPerDepth}px)` }}
              >
                <span className={styles.disclosureRow}>
                  {hasChildren ? (
                    <button
                      type="button"
                      className={`${styles.disclosureBtn} ${
                        isExpanded ? styles.disclosureBtnOpen : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleExpand(id);
                      }}
                      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                      aria-expanded={isExpanded}
                    >
                      <Icon name="chevron-right" size={12} />
                    </button>
                  ) : (
                    <span className={styles.disclosureSpacer} aria-hidden="true" />
                  )}
                  <span className={styles.disclosureContent}>{content}</span>
                </span>
              </td>
            );
          })}
        </tr>,
      );

      if (isExpanded) {
        const childFragment = renderRowTree(children, depth + 1, trKey);
        if (childFragment) fragments.push(...childFragment);
      }
    });

    return fragments;
  };

  // Table width derivation:
  //   1. After any column resize → explicit pixel width (sum of
  //      committed column widths) + minWidth: 100% so the table still
  //      fills narrow viewports.
  //   2. When all column widths are numeric (pixels) from the start →
  //      same treatment, computed off the schema. Lets consumers extend
  //      a table beyond the container width and pick up horizontal
  //      scroll via `.tableScroll`. (Step 5 attack-path table uses this
  //      to surface 20 ISO 21434 Annex G fields.)
  //   3. Otherwise → undefined; CSS `.table { width: 100% }` kicks in.
  const hasResized = Object.keys(colWidths).length > 0;
  const allColumnsPixelSized =
    !hasResized && columns.length > 0 && columns.every((c) => typeof c.width === 'number');
  let tableStyle;
  if (hasResized) {
    tableStyle = {
      width: Object.values(colWidths).reduce((sum, w) => sum + w, 0) + (selectable ? 44 : 0),
      minWidth: '100%',
    };
  } else if (allColumnsPixelSized) {
    tableStyle = {
      width: columns.reduce((sum, c) => sum + (c.width || 0), 0) + (selectable ? 44 : 0),
      minWidth: '100%',
    };
  }

  return (
    <div className={`${styles.wrapper} ${className}`}>
      {toolbar && <div className={styles.toolbarSlot}>{toolbar}</div>}

      <div ref={scrollRef} className={styles.tableScroll}>
        <table ref={tableRef} className={styles.table} style={tableStyle}>
          <thead>
            <tr>
              {selectable && (
                <th className={`${styles.headerCell} ${styles.checkboxCell}`}>
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className={styles.checkbox}
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map((column, colIdx) => {
                const isSorted = sort?.column === column.key;
                const direction = isSorted ? sort.direction : null;
                const ariaSort = !isSorted
                  ? 'none'
                  : direction === 'asc'
                  ? 'ascending'
                  : 'descending';
                const classes = [
                  styles.headerCell,
                  column.sortable ? styles.sortable : '',
                  isSorted ? styles.sorted : '',
                  direction === 'desc' ? styles.desc : '',
                  column.align ? styles[`align-${column.align}`] : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                // Use pixel width if column has been resized, otherwise original prop
                const widthStyle = colWidths[column.key]
                  ? { width: colWidths[column.key] }
                  : column.width
                  ? { width: column.width }
                  : undefined;

                // No resize handle on the last column — the splitter
                // logic treats it as the filler.
                const isLastColumn = colIdx === columns.length - 1;

                return (
                  <th
                    key={column.key}
                    className={classes}
                    style={widthStyle}
                    title={column.headerTitle}
                    aria-sort={column.sortable ? ariaSort : undefined}
                    onClick={() => handleHeaderClick(column)}
                    onKeyDown={(e) => handleHeaderKeyDown(e, column)}
                    tabIndex={column.sortable ? 0 : undefined}
                    role={column.sortable ? 'button' : undefined}
                  >
                    <div className={styles.headerContent}>
                      <span className={styles.headerLabel}>{column.label}</span>
                      {column.sortable && (
                        <Icon name="arrow-up" className={styles.sortIcon} />
                      )}
                    </div>
                    {!isLastColumn && (
                      <div
                        className={styles.resizeHandle}
                        onMouseDown={(e) => onResizeStart(e, column.key)}
                        onClick={(e) => e.stopPropagation()}
                        role="separator"
                        aria-orientation="vertical"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, rowIdx) => (
                <tr key={`skel-${rowIdx}`} className={styles.skeletonRow}>
                  {selectable && (
                    <td className={`${styles.cell} ${styles.checkboxCell}`}>
                      <Skeleton className={styles.skeletonBox} style={{ width: 16, height: 16 }} />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={styles.cell} style={{ width: col.width }}>
                      <Skeleton
                        className={styles.skeletonBar}
                        style={{ width: col.key === 'actions' ? '40%' : `${55 + (rowIdx * 7) % 30}%` }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} className={styles.stateCell}>
                  <Empty>
                    <EmptyHeader>
                      {emptyState?.icon && (
                        <EmptyMedia variant="icon">{emptyState.icon}</EmptyMedia>
                      )}
                      <EmptyTitle>{emptyState?.title ?? emptyMessage}</EmptyTitle>
                      {emptyState?.description && (
                        <EmptyDescription>{emptyState.description}</EmptyDescription>
                      )}
                    </EmptyHeader>
                  </Empty>
                </td>
              </tr>
            ) : (
              renderRowTree(data, 0)
            )}
          </tbody>
        </table>
      </div>

      {pagination && <div className={styles.paginationSlot}>{pagination}</div>}
    </div>
  );
};

export default DataTable;
